import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { parseQueryString } from 'utils/url';
import { adjustTo5MinuteIntervals, qRangeToTs } from 'utils/datetime';
import { prepareTimerangeQuery, prepareTimerangeQueryWithSeconds } from 'utils/pql';
import type { Seconds } from 'types/time';

type QsValue = string | number | string[] | undefined;
type TimeBoundaries = { t0: Seconds | null; t1: Seconds | null };

const getTimeBoundaries = (q: QsValue, from: QsValue, to: QsValue, defaultTimerangeString: string): TimeBoundaries => {
  if (from && to) {
    return {
      t0: ((from as number) / 1000) as Seconds,
      t1: ((to as number) / 1000) as Seconds,
    };
  }

  if (!q) {
    return qRangeToTs(defaultTimerangeString);
  }

  return qRangeToTs(q as string);
};

const useTimerange = (
  defaultTimerangeString = 'last 2 hours',
  { shouldAdjustTo5MinIntervals = false }: { shouldAdjustTo5MinIntervals?: boolean } = {},
) => {
  const location = useLocation();
  const qs = parseQueryString(location.search);

  return useMemo(() => {
    const timeBoundaries = getTimeBoundaries(qs.q, qs.from, qs.to, defaultTimerangeString);

    const pqlTimerange =
      qs.q ||
      (timeBoundaries.t0 &&
        timeBoundaries.t1 &&
        prepareTimerangeQuery(timeBoundaries.t0 * 1000, timeBoundaries.t1 * 1000));

    let adjustedT0 = timeBoundaries.t0;
    let adjustedT1 = timeBoundaries.t1;
    if (shouldAdjustTo5MinIntervals) {
      const { roundedFrom, roundedTo } = adjustTo5MinuteIntervals(
        timeBoundaries.t0 as Seconds,
        timeBoundaries.t1 as Seconds,
      );

      adjustedT0 = roundedFrom;
      adjustedT1 = roundedTo;
    }

    return {
      from: qs.from,
      to: qs.to,
      q: qs.q,

      t0: adjustedT0,
      t1: adjustedT1,
      pqlTimerange: shouldAdjustTo5MinIntervals
        ? prepareTimerangeQueryWithSeconds((adjustedT0 as Seconds) * 1000, (adjustedT1 as Seconds) * 1000)
        : pqlTimerange,
    };
  }, [qs.q, qs.from, qs.to, defaultTimerangeString, shouldAdjustTo5MinIntervals]);
};

export default useTimerange;
